import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Button,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Tabs,
    Tab,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import UnarchiveRoundedIcon from '@mui/icons-material/UnarchiveRounded';
import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';
import { useConfirm } from '../../components/ConfirmDialog';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

export default function AcademicYearsPage() {
    const confirm = useConfirm();
    const [tab, setTab] = useState('active'); // 'active' | 'archived'
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [label, setLabel] = useState('');
    const [startsOn, setStartsOn] = useState('');
    const [endsOn, setEndsOn] = useState('');
    const [seedTerms, setSeedTerms] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const qs = tab === 'archived' ? '?archived=true' : '';
            const { data } = await axios.get(`${API_URL}/api/admin/academic-years${qs}`, authHeaders());
            setItems(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [tab]);

    const openCreate = () => {
        setEditing(null);
        setLabel('');
        setStartsOn('');
        setEndsOn('');
        setSeedTerms(true);
        setDialogOpen(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setLabel(item.label || '');
        setStartsOn(item.startsOn ? item.startsOn.slice(0, 10) : '');
        setEndsOn(item.endsOn ? item.endsOn.slice(0, 10) : '');
        setSeedTerms(false);
        setDialogOpen(true);
    };

    const closeDialog = () => setDialogOpen(false);

    const save = async () => {
        if (!label.trim() || !startsOn || !endsOn) {
            setError('Label, start and end are required.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            if (editing) {
                await axios.patch(
                    `${API_URL}/api/admin/academic-years/${editing._id}`,
                    { label: label.trim(), startsOn, endsOn },
                    authHeaders()
                );
                setMessage('Academic year updated.');
            } else {
                await axios.post(
                    `${API_URL}/api/admin/academic-years`,
                    { label: label.trim(), startsOn, endsOn, seedTerms },
                    authHeaders()
                );
                setMessage(seedTerms ? 'Academic year + three empty terms created.' : 'Academic year created.');
            }
            closeDialog();
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setSaving(false);
        }
    };

    const activate = async (id) => {
        try {
            await axios.post(`${API_URL}/api/admin/academic-years/${id}/activate`, {}, authHeaders());
            setMessage('Active year updated.');
            await load();
        } catch {
            setError('Failed to set active.');
        }
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete academic year', message: 'Prefer Archive if you\'ve run classes against it — archive preserves the history.', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/academic-years/${id}`, authHeaders());
            setMessage('Deleted.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    const archive = async (id) => {
        if (!(await confirm({ title: 'Archive academic year', message: 'It stays in the database (terms, classes and lessons untouched) but is hidden from the default list and can\'t be marked active until restored.', confirmLabel: 'Archive' }))) return;
        try {
            await axios.post(`${API_URL}/api/admin/academic-years/${id}/archive`, {}, authHeaders());
            setMessage('Archived.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to archive.');
        }
    };

    const unarchive = async (id) => {
        try {
            await axios.post(`${API_URL}/api/admin/academic-years/${id}/unarchive`, {}, authHeaders());
            setMessage('Restored. Mark it active if you want it as the default year again.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to restore.');
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Term dates</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                        Every tutoring class runs inside an academic year with three terms. The active
                        year is the calendar admin, tutors, parents and students all work against.
                    </Typography>
                    
                </Box>
                <Button
                    onClick={openCreate}
                    variant="contained"
                    sx={{ padding: '8px 22px', fontFamily: '"Inter"', fontWeight: 500, color: '#fff !important' }}
                >
                    New academic year
                </Button>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1. Create a year</strong> each September (or whenever your teaching cycle
                            starts). Give it a label (e.g. <em>2026/27</em>), the overall start and end
                            dates, and tick <em>Seed empty terms</em> to auto-create Term 1 / 2 / 3 rows
                            you'll fill in.<br />
                            <strong>2. Mark one year as active</strong> using the star icon. All new
                            tutoring classes, lessons and calendar broadcasts default to that year;
                            activating a year automatically deactivates the previous one.<br />
                            <strong>3. Open <em>Manage terms</em></strong> on a year to set each term's
                            date range, add blocked weeks (half-term, Easter, bank holidays) that lesson
                            generation should skip, and broadcast the calendar to everyone.<br />
                            <strong>4. Archive an old year</strong> once it's no longer live — the year
                            moves to the <em>Archived</em> tab, keeps all its history intact, and stops
                            appearing in class-creation dropdowns.<br />
                            <strong>5. Delete</strong> only if the year has no terms and no history worth
                            keeping. Once a year has been used to run classes, prefer archive.
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                    <Tab value="active" label="Active" />
                    <Tab value="archived" label="Archived" />
                </Tabs>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Label</TableCell>
                                <TableCell>Starts</TableCell>
                                <TableCell>Ends</TableCell>
                                <TableCell>{tab === 'active' ? 'Active' : 'Archived on'}</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((y) => (
                                <TableRow key={y._id}>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography>{y.label}</Typography>
                                            {y.isActive && <Chip size="small" label="Active" color="success" />}
                                            {y.isArchived && <Chip size="small" label="Archived" color="default" />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{fmtDate(y.startsOn)}</TableCell>
                                    <TableCell>{fmtDate(y.endsOn)}</TableCell>
                                    <TableCell>
                                        {tab === 'active' ? (
                                            <IconButton onClick={() => !y.isActive && activate(y._id)} title={y.isActive ? 'Active year' : 'Set as active'}>
                                                {y.isActive ? <StarIcon color="warning" /> : <StarBorderIcon />}
                                            </IconButton>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {y.archivedAt ? fmtDate(y.archivedAt) : '—'}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            component={RouterLink}
                                            to={`/academic-years/${y._id}/terms`}
                                            size="small"
                                        >
                                            Manage terms
                                        </Button>
                                        {tab === 'active' ? (
                                            <>
                                                <IconButton onClick={() => openEdit(y)} title="Edit"><EditIcon /></IconButton>
                                                <Tooltip title="Archive — hide from default list, preserve history">
                                                    <IconButton onClick={() => archive(y._id)}>
                                                        <Inventory2RoundedIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <IconButton onClick={() => remove(y._id)} title="Delete permanently"><DeleteIcon /></IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <Tooltip title="Restore from archive">
                                                    <IconButton onClick={() => unarchive(y._id)}>
                                                        <UnarchiveRoundedIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <IconButton onClick={() => remove(y._id)} title="Delete permanently"><DeleteIcon /></IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            {tab === 'active'
                                                ? 'No academic years yet. Create one to start setting term dates.'
                                                : 'No archived years yet. Archived years appear here when you\'re done running classes against them.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Edit academic year' : 'New academic year'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Label"
                            placeholder="2026/27"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            size="small"
                        />
                        <TextField
                            label="Starts on"
                            type="date"
                            value={startsOn}
                            onChange={(e) => setStartsOn(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                        <TextField
                            label="Ends on"
                            type="date"
                            value={endsOn}
                            onChange={(e) => setEndsOn(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                        {!editing && (
                            <FormControlLabel
                                control={<Checkbox checked={seedTerms} onChange={(e) => setSeedTerms(e.target.checked)} />}
                                label="Pre-create Term 1, Term 2 and Term 3 (you can set their dates next)"
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
                    <Button onClick={save} variant="contained" disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
